import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CornerDownLeft, FileText, Layers, LifeBuoy, Moon, Sparkles } from 'lucide-react';
import { useUserSettingsContext } from '../../../context/UserSettingsContext';
import { useTheme } from '../../../context/ThemeContext';
import {
  NATIVE_REPORTS_CATALOG,
  filterCatalogByReportGroups,
  resolveNativeReportGroups,
} from '../../../config/nativeReportsCatalog';
import { useReportCatalogFlags } from '../../../hooks/useReportCatalogFlags';
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from '../../ui/command';
import { openAssistant, openSupport } from './shellEvents';
import PaletteHelpAnswer from './PaletteHelpAnswer';
import { api } from '../../../utils/api';
import API_ENDPOINTS from '../../../config/apiConfig';

const VERSION_STATUS = { published: 'Published', replaced: 'Replaced', locked: 'Locked', draft: 'Draft' };

/**
 * Forecast and budget versions matching the typed text, across every property
 * the user can open — the server applies property access and version sharing,
 * so private drafts only come back for their owner, the people they're shared
 * with and admins. Only for people who can open Budget + Forecast at all.
 */
function useVersionSearch({ open, enabled, orgId, query }) {
  const [versions, setVersions] = useState([]);
  useEffect(() => {
    const q = query.trim();
    if (!open || !enabled || !orgId || q.length < 2) {
      setVersions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ orgId: String(orgId), q, limit: '8' });
        const res = await api.get(`${API_ENDPOINTS.FORECAST_V3_VERSIONS_AI}/search?${params}`);
        if (!cancelled) setVersions(res.data?.versions || []);
      } catch {
        if (!cancelled) setVersions([]);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, enabled, orgId, query]);
  return versions;
}

const isTypingTarget = (el) =>
  el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

/**
 * ⌘K / Ctrl+K (and "/" outside inputs): jump to any page, report or
 * organization. Replaces the floating tray's report search.
 */
export default function CommandPalette({ pages, nav }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const { currentOrganization, userSettings, organizations, setCurrentOrg } = useUserSettingsContext();
  const catalogFlags = useReportCatalogFlags();
  const { toggleDarkMode } = useTheme();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.key || '').toLowerCase() === 'k' && (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    const onOpen = () => setOpen(true);
    document.addEventListener('keydown', onKey);
    window.addEventListener('lodgeic:command-palette:open', onOpen);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('lodgeic:command-palette:open', onOpen);
    };
  }, []);

  // Report pages with their catalog blurbs, gated by the same report grants.
  const reports = useMemo(() => {
    const groups = resolveNativeReportGroups(currentOrganization, userSettings);
    return filterCatalogByReportGroups(NATIVE_REPORTS_CATALOG, groups, catalogFlags).flatMap((g) =>
      g.pages.map((p) => ({ ...p, group: g.group }))
    );
  }, [currentOrganization, userSettings, catalogFlags]);

  const reportPaths = useMemo(() => new Set(reports.map((r) => r.path)), [reports]);
  const otherPages = useMemo(
    () => pages.filter((p) => !reportPaths.has(p.to.split('?')[0])),
    [pages, reportPaths]
  );

  const sections = useMemo(() => {
    const map = new Map();
    otherPages.forEach((p) => {
      if (!map.has(p.section)) map.set(p.section, []);
      map.get(p.section).push(p);
    });
    return [...map.entries()];
  }, [otherPages]);

  const orgList = useMemo(
    () => (Array.isArray(organizations) && organizations.length > 1
      ? [...organizations].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      : []),
    [organizations]
  );

  const aiEnabled = nav.main.some((g) => g.items.some((i) => i.action === 'assistant'));

  // Same gate as the sidebar: no Budget + Forecast page, no versions in search.
  const canForecast = pages.some((p) => (p.to || '').split('?')[0] === '/forecast-v3');
  const versions = useVersionSearch({ open, enabled: canForecast, orgId: currentOrganization?.id, query });
  useEffect(() => { if (!open) setQuery(''); }, [open]);

  const openVersion = (v) => {
    const params = new URLSearchParams({ tab: 'workspace', location: String(v.locationId), version: String(v.id) });
    if (v.year) params.set('year', String(v.year));
    navigate(`/forecast-v3?${params}`, { state: { forecastJump: Date.now() } });
  };

  const run = (fn) => {
    setOpen(false);
    fn();
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} label="Search Lodgeic">
      <CommandInput value={query} onValueChange={setQuery} placeholder={canForecast ? 'Search reports, pages, budgets, forecasts…' : 'Search reports, pages, organizations…'} />
      <CommandList>
        {/* Nothing matches: the Help Center answers the typed text instead. */}
        <CommandEmpty className="p-0">
          <PaletteHelpAnswer
            query={query}
            onOpenHelp={(q, seed) => run(() => navigate(`/help?q=${encodeURIComponent(q)}&ask=1`, { state: seed ? { helpAnswer: seed } : undefined }))}
            onLeave={() => setOpen(false)}
          />
        </CommandEmpty>

        {reports.length > 0 && (
          <CommandGroup heading="Reports">
            {reports.map((r) => {
              const Icon = r.icon || FileText;
              return (
                <CommandItem key={r.key} value={`report ${r.group} ${r.title} ${r.blurb || ''}`} onSelect={() => run(() => navigate(r.path))}>
                  <Icon />
                  <span>{r.title}</span>
                  <span className="truncate text-xs text-[var(--lodge-onyx-400)]">{r.group}</span>
                  <CommandShortcut><CornerDownLeft className="h-3 w-3" /></CommandShortcut>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {versions.length > 0 && (
          <CommandGroup heading="Budgets & forecasts">
            {versions.map((v) => (
              <CommandItem
                key={`version-${v.id}`}
                // Server already matched; carry the typed text so the client filter keeps the row.
                value={`version ${v.id} ${v.name} ${v.locationName || ''} ${v.versionType} ${v.year || ''} ${query}`}
                onSelect={() => run(() => openVersion(v))}
              >
                <Layers />
                <span className="truncate">{v.name}</span>
                <span className="truncate text-xs text-[var(--lodge-onyx-400)]">
                  {[v.locationName, v.year ? `FY ${v.year}` : null].filter(Boolean).join(' · ')}
                </span>
                <span className="ml-auto flex flex-none items-center gap-1.5 text-[11px] font-medium">
                  <span className={v.status === 'published' ? 'text-[var(--color-success-ink)]' : 'text-[var(--lodge-onyx-400)]'}>
                    {VERSION_STATUS[v.status] || v.status}
                  </span>
                  <span
                    className="rounded px-1.5 py-px font-semibold"
                    style={v.versionType === 'budget'
                      ? { background: 'var(--lodge-orange-100)', color: 'var(--lodge-orange-600)' }
                      : { background: 'var(--color-info-bg)', color: 'var(--color-info-ink)' }}
                  >
                    {v.versionType === 'budget' ? 'Budget' : 'Forecast'}
                  </span>
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {sections.map(([section, items]) => (
          <CommandGroup key={section} heading={section}>
            {items.map((p) => (
              <CommandItem key={p.key} value={`${section} ${p.parent || ''} ${p.title}`} onSelect={() => run(() => navigate(p.to))}>
                <FileText />
                <span>{p.parent ? `${p.parent} · ${p.title}` : p.title}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        ))}

        <CommandSeparator />
        <CommandGroup heading="Actions">
          {aiEnabled && (
            <CommandItem value="ask lodgeic ai assistant" onSelect={() => run(openAssistant)}>
              <Sparkles />
              <span>Ask Lodgeic AI</span>
            </CommandItem>
          )}
          <CommandItem value="toggle dark mode theme" onSelect={() => run(toggleDarkMode)}>
            <Moon />
            <span>Toggle dark mode</span>
          </CommandItem>
          <CommandItem value="contact support help intercom" onSelect={() => run(openSupport)}>
            <LifeBuoy />
            <span>Contact support</span>
          </CommandItem>
        </CommandGroup>

        {orgList.length > 0 && (
          <CommandGroup heading="Switch organization">
            {orgList.map((org) => (
              <CommandItem
                key={org.code}
                value={`organization ${org.name} ${org.code}`}
                onSelect={() => run(() => {
                  setCurrentOrg(org.code);
                  navigate('/dashboard');
                })}
              >
                <Building2 />
                <span>{org.name}</span>
                {currentOrganization?.code === org.code && (
                  <span className="ml-auto text-xs text-[var(--lodge-orange-500)]">Current</span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}
